import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { isBefore, format } from 'date-fns';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ControlledToaster } from '@components/Toaster';
import { useTranslation, useQueryParams, useLocations } from '@hooks';
import { Trans } from 'react-i18next';
import { reviewsService } from '@services';
import {
    reviewActions,
    replyTemplatesActions,
    themesActions,
    accountActions,
    contactsActions,
    labelsActions,
    channelsActions,
} from '@actions';
import ExportButton from '@features/ExportButton';
import ContactDrawer from '@features/ContactDrawer';
import { FiltersIntegrationType } from '@constants/integrations';
import { getThemesFilterOptions } from '@helpers/filters';
import FiltersPanel from './FiltersPanel';
import Reviews from './Reviews';

const Container = styled('div')({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const MINUTE = 1000 * 60;

const ReviewsPage = () => {
    const { t } = useTranslation();
    const params = useQueryParams();
    const navigate = useNavigate();
    const { companies } = useSelector(state => state.account?.account);
    const { reviews: reviewsList = [], pageToken, loading, newReviewsExist } = useSelector(state => state.reviews);
    const { data: themesData, loading: themesLoading } = useSelector(state => state.themes);
    const language = useSelector(state => state.auth.user?.language);
    const { contact } = useSelector(state => state.contacts);

    const queryParamsReviewId = params.get('reviewId');
    const themeParam = params.get('theme');
    const ratingParam = params.get('rating');
    const fromParam = params.get('from');
    const toParam = params.get('to');
    const companyIdParam = params.get('companyId');

    const [filter, setFilter] = useState({
        companies: [],
        integrations: params.get('integrationNames') ? JSON.parse(params.get('integrationNames')) : [],
        timeframe: {
            start: fromParam ? new Date(fromParam) : null,
            end: toParam ? new Date(toParam) : null,
        },
        rating: ratingParam ? ratingParam.split(',').map(Number) : [1, 2, 3, 4, 5],
        keywords: params.get('keyword') ? [params.get('keyword')] : [],
        ignoreKeywords: params.get('keyword') ? false : true,
        themes: [],
        ignoreThemes: themeParam ? false : true,
        withoutReplies: params.get('withoutReplies') ? Boolean(params.get('withoutReplies')) : false,
        onlyFeatured: params.get('onlyFeatured') === 'true',
        reviewId: queryParamsReviewId ? queryParamsReviewId : undefined,
    });
    const [openAlert, setOpenAlert] = useState(false);
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const [applyChipsSignal, setApplyChipsSignal] = useState(0);

    const { mapLocationsToFormValue, locationsMap } = useLocations();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const dispatch = useDispatch();

    const firstRender = useRef(true);

    useEffect(() => {
        dispatch(themesActions.getAll());
        dispatch(replyTemplatesActions.getAll());
        dispatch(labelsActions.get());
        dispatch(channelsActions.get());
    }, [dispatch]);

    const availableCompanies = useMemo(
        () =>
            companies.filter(
                company =>
                    company?.profiles?.length > 0 && company?.profiles.some(p => p.type in FiltersIntegrationType),
            ),
        [companies],
    );

    useEffect(() => {
        if (availableCompanies.length && !themesLoading) {
            const integrations = [
                ...new Set(
                    availableCompanies
                        .map(company => company.profiles)
                        .flat()
                        .filter(p => p.type in FiltersIntegrationType)
                        .map(p => p.type),
                ),
            ];
            let companiesToUse = availableCompanies;
            if (companyIdParam) {
                const specificCompany = availableCompanies.filter(c => c.companyId === parseInt(companyIdParam));
                if (specificCompany.length) companiesToUse = specificCompany;
            }
            dispatch(reviewActions.request());
            setFilter(prev => ({
                ...prev,
                integrations,
                companies: mapLocationsToFormValue(companiesToUse),
                themes: themeParam ? [themeParam] : [],
                rating: ratingParam ? ratingParam.split(',').map(Number) : [1, 2, 3, 4, 5],
                timeframe: {
                    start: fromParam ? new Date(fromParam) : null,
                    end: toParam ? new Date(toParam) : null,
                },
                ignoreThemes: !themeParam,
            }));
            firstRender.current = true;
            const queryParamsFilter = [
                params.get('keyword'),
                params.get('withoutReplies'),
                params.get('integrationNames'),
                themeParam,
                ratingParam,
                fromParam,
                companyIdParam,
            ];
            setFilterNumber(queryParamsFilter.filter(Boolean).length);
        } else if (!availableCompanies.length && !themesLoading) {
            dispatch(reviewActions.resetLoading());
        }
    }, [
        availableCompanies,
        themesLoading,
        themesData,
        dispatch,
        companyIdParam,
        themeParam,
        ratingParam,
        fromParam,
        toParam,
    ]);

    const buildQuery = f => ({
        ...f,
        companies: f.companies.length ? f.companies.map(c => c.id) : [],
        integrationNames: f.integrations,
        from: f.timeframe.start ? format(new Date(f.timeframe.start), 'dd/MMM/yyyy') : null,
        to: f.timeframe.end ? format(new Date(f.timeframe.end), 'dd/MMM/yyyy') : null,
    });

    const currentQuery = useMemo(() => buildQuery(filter), [filter]);

    useEffect(() => {
        if (queryParamsReviewId && !firstRender.current) {
            dispatch(reviewActions.getAll({ ...currentQuery, reviewId: queryParamsReviewId }));
            navigate({ search: '' }, { replace: true });
        }
    }, [queryParamsReviewId]);

    useEffect(() => {
        if (currentQuery.companies.length && currentQuery.integrationNames.length && currentQuery.rating.length) {
            if (firstRender.current) {
                dispatch(reviewActions.getAll(currentQuery));
                firstRender.current = false;
            }
        }
    }, [currentQuery, dispatch]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                const newExists = await reviewsService.checkNewExist({ ...currentQuery, reviewId: undefined });
                dispatch(reviewActions.checkNewExistSuccess(newExists));
            } catch (_) {
                logger.error('Failed to check new reviews');
            }
        }, MINUTE);
        return () => clearInterval(interval);
    }, [currentQuery, dispatch]);

    useEffect(() => {
        const feedType = getFeedType();
        if (newReviewsExist && feedType === 2) {
            getNewReviews();
            dispatch(accountActions.get());
        } else if (newReviewsExist) {
            setOpenAlert(true);
        }
    }, [newReviewsExist]);

    const getNewReviews = () => {
        setOpenAlert(false);
        dispatch(reviewActions.resetNewExist());
        dispatch(reviewActions.getAll({ ...currentQuery, reviewId: undefined }));
    };

    const onCloseToaster = () => dispatch(reviewActions.resetNewExist());

    const loadMore = () => {
        if (!pageToken) return;
        dispatch(reviewActions.loadMore(currentQuery, pageToken));
    };

    const onApplyFilters = () => {
        dispatch(reviewActions.getAll({ ...currentQuery, reviewId: undefined }));
    };

    const getFeedType = () => {
        if (!filter.companies.length || !filter.integrations.length || !filter.rating.length) {
            return 1;
        }
        const isScraping = companies
            .filter(c => filter.companies.some(fC => fC.id === c.companyId))
            .map(item => item.profiles)
            .flat()
            .filter(c => filter.integrations.includes(c.type))
            .every(c => isBefore(new Date(c.lastScrapedTime), new Date(2000, 1, 1)));
        if (isScraping && !reviewsList.length && !firstRender.current) {
            return 2;
        }
        if (!reviewsList.length && !firstRender.current) {
            return 3;
        }
        return 0;
    };

    const onClickContactDetails = contactId => {
        if (contactId !== contact?.id) {
            dispatch(contactsActions.getContact(contactId));
        }
        dispatch(contactsActions.setContactDrawer(true));
    };

    const onUpdateContactCallback = () => {
        dispatch(reviewActions.invalidateReviews(currentQuery, null, reviewsList.length));
    };

    const onFilterByLocation = locationId => {
        const company = companies.find(c => c.companyId === locationId);
        if (!company) return;
        const locationData = locationsMap[locationId];
        const availableIntegrations =
            locationData?.profiles?.filter(p => p.type in FiltersIntegrationType).map(p => p.type) || [];
        const narrowedIntegrations = filter.integrations.filter(i => availableIntegrations.includes(i));
        const availableThemes = getThemesFilterOptions(themesData, [locationId]).map(th => th.value);
        const narrowedThemes = filter.themes.filter(th => availableThemes.includes(th));
        const updatedFilter = {
            ...filter,
            companies: mapLocationsToFormValue([company]),
            integrations: narrowedIntegrations.length ? narrowedIntegrations : availableIntegrations,
            themes: narrowedThemes,
            ignoreThemes: narrowedThemes.length === 0,
            reviewId: undefined,
        };
        setFilter(updatedFilter);
        dispatch(reviewActions.getAll(buildQuery(updatedFilter)));
        setApplyChipsSignal(prev => prev + 1);
    };

    return (
        <Container>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    themes={themesData}
                    filter={filter}
                    setFilter={setFilter}
                    type="reviews"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                    applyChipsSignal={applyChipsSignal}
                />
                <ExportButton callback={format => reviewsService.exportCSV(currentQuery, format)} type="reviews" />
            </div>
            <Reviews
                isMobile={isMobile}
                data={reviewsList}
                language={language}
                pageToken={pageToken}
                loading={loading}
                companies={companies}
                fetchData={loadMore}
                feedType={getFeedType()}
                onClickContactDetails={onClickContactDetails}
                onFilterByLocation={onFilterByLocation}
            />
            <ControlledToaster
                callback={getNewReviews}
                autoHideDuration={null}
                text={
                    <Trans t={t} i18nKey={'ReviewsManage.newReviews'}>
                        <b style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={getNewReviews}>
                            {{ update: t('ReviewsManage.update') }}
                        </b>
                    </Trans>
                }
                type="info"
                open={openAlert}
                setOpen={setOpenAlert}
                clickClose={onCloseToaster}
            />
            <ContactDrawer onUpdateCallback={onUpdateContactCallback} />
        </Container>
    );
};

export default ReviewsPage;
